/*
  Отменённый приказ - сведения, которые иск назовёт суду (§6.4.5, вариант А
  макетов, выбран владельцем 07.10.2026).

  Номер и дата приказа приходят из реестра приказов, их оператор только
  проверяет. Дополняет он то, чего система знать не может: кто и когда отменил
  приказ, месяцы приказа, день подачи и платёжку за приказ.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { money } from '../api/format.js'
import { Field, Notice } from './Common.jsx'
import { asInput } from './entry/money.js'

/* Поля формы строками - так их держат поля ввода; на сервер уходят значениями. */
function initial(facts) {
  return {
    order_number: facts?.order_number ?? '',
    order_date: facts?.order_date ?? '',
    cancelled_on: facts?.cancelled_on ?? '',
    court: facts?.court ?? '',
    period_from: facts?.period_from?.slice(0, 7) ?? '',
    period_through: facts?.period_through?.slice(0, 7) ?? '',
    filed_on: facts?.filed_on ?? '',
    // Сумма с сервера приходит с точкой («3930.00»); в поле - по-русски, с запятой.
    fee_paid: asInput(facts?.fee_paid),
    payment_document: facts?.payment_document ?? '',
  }
}

/* «3 930,00» и «3930.00» - одно и то же число; пусто - не внесено. */
function amount(text) {
  const cleaned = String(text).replace(/\s/g, '').replace(',', '.')
  return cleaned === '' ? null : cleaned
}

function body(values) {
  const month = (value) => (value ? `${value}-01` : null)
  return {
    order_number: values.order_number.trim() || null,
    order_date: values.order_date || null,
    cancelled_on: values.cancelled_on,
    court: values.court.trim() || null,
    period_from: month(values.period_from),
    period_through: month(values.period_through),
    filed_on: values.filed_on || null,
    fee_paid: amount(values.fee_paid),
    payment_document: values.payment_document.trim() || null,
  }
}

export default function CancelledOrderForm({ accountId, facts, feeHint, onSaved }) {
  const [values, setValues] = useState(() => initial(facts))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const set = (name) => (event) => setValues({ ...values, [name]: event.target.value })
  const input = (name, type = 'text') => (
    <input className="field__input" type={type} value={values[name]} onChange={set(name)} />
  )

  async function save(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.saveCancellation(accountId, body(values))
      onSaved('Сведения об отменённом приказе сохранены.')
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section card">
      <h2 className="section__title">Отменённый приказ</h2>
      <div className="muted">
        Номер и дата взяты из реестра приказов. Проверьте и дополните - иск назовёт их суду.
      </div>
      <form onSubmit={save}>
        <div className="row row--fields">
          <Field label="Номер приказа" width="s">{input('order_number')}</Field>
          <Field label="Дата приказа" width="s">{input('order_date', 'date')}</Field>
          <Field label="Отменён" width="s">{input('cancelled_on', 'date')}</Field>
          <Field
            label="Кто отменил"
            width="l"
            hint="Как суд назван в определении об отмене: «мировой судья судебного участка № 1 ...». Иск повторит эти слова."
          >
            {input('court')}
          </Field>
        </div>
        <div className="row row--fields">
          <Field
            label="Месяцы приказа: с"
            width="s"
            hint="Иск предъявляет ровно месяцы приказа: месяцы сверх него сначала взыскиваются приказом."
          >
            {input('period_from', 'month')}
          </Field>
          <Field label="по" width="s">{input('period_through', 'month')}</Field>
          <Field
            label="Заявление подано"
            width="s"
            hint="День подачи заявления о приказе. Пока шло приказное производство, срок давности не тёк."
          >
            {input('filed_on', 'date')}
          </Field>
        </div>
        <div className="row row--fields">
          <Field
            label="Пошлина за приказ, ₽"
            width="s"
            hint="Сумма по платёжке. В иске её зачтут, и доплатить придётся только разницу."
          >
            {input('fee_paid')}
          </Field>
          <Field label="Платёжное поручение" width="l">
            {input('payment_document')}
          </Field>
          <button
            className="button button--quiet"
            type="submit"
            disabled={busy || !values.cancelled_on}
          >
            Сохранить сведения
          </button>
        </div>
      </form>
      {feeHint ? (
        <div className="muted">
          По расчёту пакета приказа пошлина - {money(feeHint)}. Внесите сумму по платёжке: без
          платёжки зачёт не заявляется.
        </div>
      ) : null}
      {error ? <Notice error={error} /> : null}
    </section>
  )
}
